import React, { useEffect, useState } from 'react';
import { LanguageLink } from './LanguageLink';
import { useI18n } from "../i18n/i18n";
import { Card, CardContent } from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { ArrowRight } from "lucide-react";

interface Article {
  id: number;
  titleKey: string;
  descriptionKey: string;
  category: string;
  imageUrl: string;
  imageAlt: string;
  href: string;
  isPremium?: boolean;
}

// Featured articles data - showing a curated selection
const featuredArticles: Article[] = [
  {
    id: 1,
    titleKey: 'home.info.title',
    descriptionKey: 'home.info.description',
    category: 'investment',
    imageUrl: '/images/articles/1554224155-6726b3ff858f',
    imageAlt: 'Compound interest concept',
    href: '/resources/compound-interest-article'
  },
  {
    id: 4,
    titleKey: 'resources.article4.title',
    descriptionKey: 'resources.article4.description',
    category: 'budgeting',
    imageUrl: '/images/articles/1633158829585-23ba8f7c8caf',
    imageAlt: 'Emergency fund concept',
    href: '/resources/emergency-fund-article'
  },
  {
    id: 14,
    titleKey: 'resources.article14.title',
    descriptionKey: 'resources.article14.description',
    category: 'technology',
    imageUrl: '/images/articles/1677442136019-21780ecad995',
    imageAlt: 'Artificial intelligence and personal finance technology',
    href: '/resources/ai-finance-article'
  },
  {
    id: 28,
    titleKey: 'resources.article28.title',
    descriptionKey: 'resources.article28.description',
    category: 'investment',
    imageUrl: '/images/articles/1579532537598-459ecdaf39cc',
    imageAlt: 'Young professional starting investment journey',
    href: '/resources/start-investing-article'
  },
  {
    id: 10,
    titleKey: 'resources.article10.title',
    descriptionKey: 'resources.article10.description',
    category: 'budgeting',
    imageUrl: '/images/articles/1579621970588-a35d0e7ab9b6',
    imageAlt: 'Monthly saving challenges',
    href: '/resources/monthly-saving-challenges-article'
  },
  {
    id: 34,
    titleKey: 'resources.article34.title',
    descriptionKey: 'resources.article34.description',
    category: 'investment',
    imageUrl: '/images/articles/1635070041078-e363dbe005cb',
    imageAlt: 'AI-powered investment strategies and automated trading algorithms',
    href: '/resources/ai-investment-article'
  },
  {
    id: 7,
    titleKey: 'resources.article7.title',
    descriptionKey: 'resources.article7.description',
    category: 'mortgages',
    imageUrl: '/images/articles/1560518883-ce09059eeffa',
    imageAlt: 'Renting vs buying concept',
    href: '/resources/renting-vs-buying-article'
  },
  {
    id: 22,
    titleKey: 'resources.article22.title',
    descriptionKey: 'resources.article22.description',
    category: 'career',
    imageUrl: '/images/articles/1507003211169-0a1dd7228f2d',
    imageAlt: 'Salary negotiation and career advancement',
    href: '/resources/salary-negotiation-article'
  },
  {
    id: 2,
    titleKey: 'resources.article2.title',
    descriptionKey: 'resources.article2.description',
    category: 'retirement',
    imageUrl: '/images/articles/1553729459-efe14ef6055d',
    imageAlt: 'Retirement planning',
    href: '/resources/retirement-planning-article'
  },
  {
    id: 8,
    titleKey: 'resources.article8.title',
    descriptionKey: 'resources.article8.description',
    category: 'debt',
    imageUrl: '/images/articles/1434626881859-194d67b2b86f',
    imageAlt: 'Inflation impact concept',
    href: '/resources/inflation-article'
  },
  {
    id: 9,
    titleKey: 'resources.article9.title',
    descriptionKey: 'resources.article9.description',
    category: 'investment',
    imageUrl: '/images/articles/1518546305927-5a555bb7020d',
    imageAlt: 'Bitcoin logo and cryptocurrency concept',
    href: '/resources/crypto-article'
  },
  {
    id: 6,
    titleKey: 'resources.article6.title',
    descriptionKey: 'resources.article6.description',
    category: 'investment',
    imageUrl: '/images/articles/1611974789855-9c2a0a7236a3',
    imageAlt: 'Investing mistakes concept',
    href: '/resources/investing-mistakes-article'
  },
  {
    id: 11,
    titleKey: 'resources.article11.title',
    descriptionKey: 'resources.article11.description',
    category: 'budgeting',
    imageUrl: '/images/articles/1556742049-0cfed4f6a45d',
    imageAlt: 'Impulse buying and psychology of money',
    href: '/resources/impulse-buying-article'
  },
  {
    id: 17,
    titleKey: 'resources.article17.title',
    descriptionKey: 'resources.article17.description',
    category: 'investment',
    imageUrl: '/images/articles/1642543492481-44e81e3914a7',
    imageAlt: 'Modern ETF and index fund investment portfolio',
    href: '/resources/etf-index-funds-article'
  },
  {
    id: 18,
    titleKey: 'resources.article18.title',
    descriptionKey: 'resources.article18.description',
    category: 'security',
    imageUrl: '/images/articles/1563013544-824ae1b704d3',
    imageAlt: 'Digital security and financial scam protection',
    href: '/resources/digital-scams-article'
  },
  {
    id: 24,
    titleKey: 'resources.article24.title',
    descriptionKey: 'resources.article24.description',
    category: 'retirement',
    imageUrl: '/images/articles/1559526324-4b87b5e36e44',
    imageAlt: 'Financial independence and realistic retirement planning concept',
    href: '/resources/financial-independence-realistic-plan-article'
  },
  {
    id: 29,
    titleKey: 'resources.article29.title',
    descriptionKey: 'resources.article29.description',
    category: 'budgeting',
    imageUrl: '/images/articles/1476703993599-0035a21b17a9',
    imageAlt: 'Happy family with newborn baby and financial planning documents',
    href: '/resources/financial-planning-before-children-article'
  },
  {
    id: 39,
    titleKey: 'resources.article39.title',
    descriptionKey: 'resources.article39.description',
    category: 'budgeting',
    imageUrl: '/images/articles/1516589178581-6cd7833ae3b2',
    imageAlt: 'Happy couple managing finances together with financial documents and laptop',
    href: '/resources/couples-finance-article'
  },
  {
    id: 40,
    titleKey: 'resources.article40.title',
    descriptionKey: 'resources.article40.description',
    category: 'career',
    imageUrl: '/images/articles/1523240795612-9a054b0db644',
    imageAlt: 'Young professional celebrating first salary success with laptop and financial documents',
    href: '/resources/first-salary-article'
  },
  {
    id: 15,
    titleKey: 'resources.article15.title',
    descriptionKey: 'resources.article15.description',
    category: 'taxPlanning',
    imageUrl: '/images/articles/1621761191319-c6fb62004040',
    imageAlt: 'Cryptocurrency taxation and compliance guide',
    href: '/resources/crypto-tax-guide-article'
  }
];

const ArticlesCarousel: React.FC = () => {
  const { t } = useI18n();
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  const autoplayPlugin = React.useRef(
    Autoplay({
      delay: 2500,
      stopOnInteraction: true,
      stopOnMouseEnter: true,
    })
  );

  useEffect(() => {
    if (!api) {
      return;
    }

    setCurrent(api.selectedScrollSnap());

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  return (
    <div className="bg-white py-16">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-neutral-800 mb-4">
            {t("home.articles.title")}
          </h2>
          <p className="text-lg text-neutral-600 max-w-3xl mx-auto leading-relaxed">
            {t("home.articles.subtitle")}
          </p>
        </div>

        <div className="relative max-w-6xl mx-auto">
          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            plugins={[autoplayPlugin.current]}
            setApi={setApi}
            className="w-full"
            onMouseEnter={() => autoplayPlugin.current.stop()}
            onMouseLeave={() => autoplayPlugin.current.play()}
          >
            <CarouselContent className="-ml-2 md:-ml-4">
              {featuredArticles.map((article) => (
                <CarouselItem key={article.id} className="pl-2 md:pl-4 md:basis-1/2 lg:basis-1/3">
                  <Card className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden hover:shadow-md transition-all h-full">
                    <LanguageLink to={article.href}>
                      <picture>
                        <source
                          type="image/avif"
                          srcSet={`${article.imageUrl}-500w.avif 500w, ${article.imageUrl}-800w.avif 800w`}
                          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                        />
                        <source
                          type="image/webp"
                          srcSet={`${article.imageUrl}-500w.webp 500w, ${article.imageUrl}-800w.webp 800w`}
                          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                        />
                        <img
                          src={`${article.imageUrl}-500w.jpg`}
                          srcSet={`${article.imageUrl}-500w.jpg 500w, ${article.imageUrl}-800w.jpg 800w`}
                          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                          alt={article.imageAlt}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-48 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                        />
                      </picture>
                    </LanguageLink>
                    <CardContent className="p-6">
                      <span className="inline-block px-2 py-1 bg-neutral-100 text-neutral-600 text-xs rounded-full mb-3">
                        {t(`resources.categories.${article.category}`)}
                      </span>
                      <h3 className="font-semibold text-xl mb-2 line-clamp-2">
                        {t(article.titleKey)}
                      </h3>
                      <p className="text-neutral-600 mb-4 line-clamp-3">
                        {t(article.descriptionKey)}
                      </p>
                      <LanguageLink to={article.href}>
                        <div className="inline-block text-[#EA580C] font-medium hover:underline cursor-pointer">
                          {t('resources.article.read')} <ArrowRight className="inline h-4 w-4 ml-1" />
                        </div>
                      </LanguageLink>
                    </CardContent>
                  </Card>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden md:flex -left-4 lg:-left-12" />
            <CarouselNext className="hidden md:flex -right-4 lg:-right-12" />
          </Carousel>

          {/* Dots indicator */}
          <div className="flex justify-center gap-2 mt-6">
            {featuredArticles.map((_, index) => (
              <button
                key={index}
                className="min-w-12 min-h-12 flex items-center justify-center rounded-full hover:bg-neutral-100 transition-colors"
                onClick={() => api?.scrollTo(index)}
                aria-label={`Go to slide ${index + 1}`}
              >
                <span
                  className={`h-2 rounded-full transition-all ${
                    index === current % featuredArticles.length
                      ? 'w-8 bg-[#EA580C]'
                      : 'w-2 bg-neutral-300'
                  }`}
                />
              </button>
            ))}
          </div>
        </div>

        {/* CTA to view all articles */}
        <div className="text-center mt-8">
          <LanguageLink to="/resources">
            <button className="inline-flex items-center px-6 py-3 bg-[#EA580C] hover:bg-[#C2410C] text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all">
              {t("home.articles.viewAll")}
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          </LanguageLink>
        </div>
      </div>
    </div>
  );
};

export default ArticlesCarousel;
